import axios from "axios";

const baseApi = process.env.REACT_APP_API_URI;

// Gerar ID unico de sessao
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Sessao unica por carregamento da pagina
const SESSION_ID = generateSessionId();

// Fila de eventos para batch
let eventQueue: TelemetryEvent[] = [];
let flushTimeout: NodeJS.Timeout | null = null;

export interface TelemetryEvent {
  sessionId: string;
  idReuniao?: string;
  idAtor?: string;
  callId?: string;
  eventName: string;
  severity: 'Info' | 'Warning' | 'Error';
  message?: string;
  properties?: Record<string, string>;
  clientTimestamp: string;
  component?: string;
  userAgent?: string;
  errorCode?: number;
  errorSubCode?: number;
}

// Funcao principal para registrar eventos
export const trackEvent = (
  eventName: string,
  severity: 'Info' | 'Warning' | 'Error' = 'Info',
  options?: {
    message?: string;
    callId?: string;
    errorCode?: number;
    errorSubCode?: number;
    properties?: Record<string, string>;
    component?: string;
  }
): void => {
  const event: TelemetryEvent = {
    sessionId: SESSION_ID,
    idReuniao: localStorage.getItem('idReuniao') || undefined,
    idAtor: localStorage.getItem('idAtor') || undefined,
    callId: options?.callId,
    eventName,
    severity,
    message: options?.message,
    properties: options?.properties,
    clientTimestamp: new Date().toISOString(),
    component: options?.component,
    userAgent: navigator.userAgent,
    errorCode: options?.errorCode,
    errorSubCode: options?.errorSubCode,
  };

  // Adicionar a fila
  eventQueue.push(event);

  // Flush imediato para erros
  if (severity === 'Error') {
    flushEvents();
    return;
  }

  // Debounce para enviar em batch
  if (flushTimeout) {
    clearTimeout(flushTimeout);
  }
  flushTimeout = setTimeout(() => flushEvents(), 2000);
};

// Envia eventos em batch
const flushEvents = async (): Promise<void> => {
  if (eventQueue.length === 0) return;

  const eventsToSend = [...eventQueue];
  eventQueue = [];

  try {
    const tokenJWT = localStorage.getItem('tokenJWT');
    await axios.post(
      `${baseApi}/Telemetria/batch`,
      { events: eventsToSend },
      {
        headers: {
          Authorization: tokenJWT ? `Bearer ${tokenJWT}` : undefined,
        },
      }
    );
  } catch (error) {
    // Silencioso - nao queremos que falha de telemetria afete o usuario
    console.warn('Falha ao enviar telemetria:', error);
    // Re-adicionar eventos na fila para tentar novamente
    eventQueue = [...eventsToSend, ...eventQueue].slice(0, 50); // Limitar fila
  }
};

// Flush antes de fechar pagina
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // Usar sendBeacon para garantir envio
    if (eventQueue.length > 0) {
      const tokenJWT = localStorage.getItem('tokenJWT');
      navigator.sendBeacon(
        `${baseApi}/Telemetria/batch`,
        JSON.stringify({ events: eventQueue })
      );
    }
  });
}

// Helpers para eventos comuns
export const telemetry = {
  // Sessao
  sessionStarted: () => trackEvent('session_started', 'Info', {
    component: 'App',
    properties: { device: localStorage.getItem('device') || 'unknown' }
  }),

  // Autenticacao
  authCompleted: () => trackEvent('auth_completed', 'Info', { component: 'Auth' }),
  authFailed: (errorCode?: number) => trackEvent('auth_failed', 'Error', {
    component: 'Auth',
    errorCode,
  }),

  // Call Adapter
  adapterCreating: () => trackEvent('call_adapter_creating', 'Info', { component: 'ChamadaContainer' }),
  adapterCreated: () => trackEvent('call_adapter_created', 'Info', { component: 'ChamadaContainer' }),
  adapterError: (message: string, errorCode?: number) => trackEvent('call_adapter_error', 'Error', {
    component: 'ChamadaContainer',
    message,
    errorCode,
  }),

  // Chamada
  callConnecting: (callId: string) => trackEvent('call_connecting', 'Info', {
    component: 'ChamadaContainer',
    callId,
  }),
  callConnected: (callId: string) => trackEvent('call_connected', 'Info', {
    component: 'ChamadaContainer',
    callId,
  }),
  callStateChanged: (callId: string, previousState: string, newState: string) => trackEvent('call_state_changed', 'Info', {
    component: 'ChamadaContainer',
    callId,
    properties: { previousState, newState },
  }),
  participantsJoined: (callId: string, count: number) => trackEvent('call_participants_joined', 'Info', {
    component: 'ChamadaContainer',
    callId,
    properties: { participantCount: count.toString() },
  }),
  participantsLeft: (callId: string, count: number) => trackEvent('call_participants_left', 'Info', {
    component: 'ChamadaContainer',
    callId,
    properties: { participantCount: count.toString() },
  }),
  callEnded: (callId: string, duration: number, endReason?: string) => trackEvent('call_ended', 'Info', {
    component: 'ChamadaContainer',
    callId,
    properties: {
      duration: duration.toString(),
      endReason: endReason || 'normal',
    },
  }),
  callEndedUnexpectedly: (callId: string, errorCode: number, errorSubCode: number) => trackEvent('call_ended_unexpectedly', 'Error', {
    component: 'ChamadaContainer',
    callId,
    errorCode,
    errorSubCode,
    message: `Call ended with code ${errorCode}.${errorSubCode}`,
  }),

  // Qualidade
  qualityWarning: (callId: string, diagnosticType: string, value: string) => trackEvent('call_quality_warning', 'Warning', {
    component: 'ChamadaContainer',
    callId,
    properties: { diagnosticType, value },
  }),

  // Token
  tokenRefreshStarted: () => trackEvent('token_refresh_started', 'Info', { component: 'TokenRefresher' }),
  tokenRefreshCompleted: () => trackEvent('token_refresh_completed', 'Info', { component: 'TokenRefresher' }),
  tokenRefreshFailed: (errorCode?: number) => trackEvent('token_refresh_failed', 'Error', {
    component: 'TokenRefresher',
    errorCode,
  }),

  // Pagina
  pageUnload: (callId?: string) => trackEvent('page_unload', 'Info', {
    component: 'App',
    callId,
  }),
};

export const getSessionId = () => SESSION_ID;
